/**
 * TrackingConversionRulesPanel — first-touch attribution conversion rules.
 * "If lead has tag X → Then send conversion Y"
 * Categories: Lead Rules, Call Rules, Sale Rules
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Plus,
  Trash2,
  Power,
  Phone,
  DollarSign,
  UserPlus,
  ArrowRight,
  X,
  Send,
  Ban,
} from "lucide-react";

interface ConversionRule {
  id: string;
  userId: string;
  name: string;
  category: string;
  conditionType: string;
  conditionValue: string | null;
  conditionTags: string[];
  actionType: string;
  actionEventName: string | null;
  actionPlatforms: string[];
  enabled: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

type RuleCategory = "lead" | "call" | "sale";

const CATEGORY_CONFIG: Record<
  RuleCategory,
  { labelKey: string; icon: typeof UserPlus; color: string }
> = {
  lead: { labelKey: "conversionRules.category.lead", icon: UserPlus, color: "text-emerald-400" },
  call: { labelKey: "conversionRules.category.call", icon: Phone, color: "text-purple-400" },
  sale: { labelKey: "conversionRules.category.sale", icon: DollarSign, color: "text-amber-400" },
};

const CONDITION_LABEL_KEYS: Record<string, string> = {
  has_tag: "conversionRules.condition.has_tag",
  tag_prefix: "conversionRules.condition.tag_prefix",
  has_product_tag: "conversionRules.condition.has_product_tag",
  is_unqualified: "conversionRules.condition.is_unqualified",
};

const ACTION_LABEL_KEYS: Record<string, string> = {
  send_with_params: "conversionRules.action.send_with_params",
  send_by_product: "conversionRules.action.send_by_product",
  do_not_send: "conversionRules.action.do_not_send",
};

const ACTION_ICONS: Record<string, typeof Send> = {
  send_with_params: Send,
  send_by_product: Send,
  do_not_send: Ban,
};

const PLATFORM_LABELS: Record<string, string> = {
  meta: "Meta",
  google_ads: "Google Ads",
  tiktok: "TikTok",
};

/**
 * VP-192: Google Ads nie jest wybieralne przy tworzeniu reguły — integracja jest
 * ukryta w UI (aplikacja bez weryfikacji Google OAuth). Etykieta zostaje w
 * PLATFORM_LABELS, żeby istniejące reguły dalej wyświetlały się po ludzku.
 */
const SELECTABLE_PLATFORMS = Object.entries(PLATFORM_LABELS).filter(
  ([key]) => key !== "google_ads"
);

// ─── Tag Input for conditions ──────────────

function ConditionTagInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (v: string[]) => void;
  placeholder: string;
}) {
  const [input, setInput] = useState("");

  const handleAdd = () => {
    const trimmed = input.trim();
    if (!trimmed || values.includes(trimmed)) return;
    onChange([...values, trimmed]);
    setInput("");
  };

  return (
    <div className="space-y-1.5">
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            handleAdd();
          }
        }}
        placeholder={placeholder}
        className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
      />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {values.map((val) => (
            <span
              key={val}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/[0.08] border border-white/[0.12] rounded text-xs text-white/80"
            >
              {val}
              <button
                onClick={() => onChange(values.filter((v) => v !== val))}
                className="text-white/40 hover:text-red-400"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Create Conversion Rule Form ───────────

function CreateConversionRuleForm({
  onCreated,
  onCancel,
}: {
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [category, setCategory] = useState<RuleCategory>("lead");
  const [conditionType, setConditionType] = useState("has_tag");
  const [conditionTags, setConditionTags] = useState<string[]>([]);
  const [conditionValue, setConditionValue] = useState("");
  const [actionType, setActionType] = useState("send_with_params");
  const [actionEventName, setActionEventName] = useState("");
  const [actionPlatforms, setActionPlatforms] = useState<string[]>(["meta"]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiRequest("POST", "/api/tracking/conversion-rules", data),
    onSuccess: onCreated,
  });

  const togglePlatform = (p: string) => {
    setActionPlatforms((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  return (
    <div className="p-4 bg-white/[0.03] border border-white/[0.1] rounded-lg space-y-4">
      <h4 className="text-xs font-medium text-white/80 flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5 text-red-400" /> {t("conversionRules.create.title")}
      </h4>

      {/* Name */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("conversionRules.create.name")}
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t("conversionRules.create.namePlaceholder")}
          className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
        />
      </div>

      {/* Category tabs */}
      <div>
        <label className="text-xs text-white/50 mb-1 block">
          {t("conversionRules.create.categoryLabel")}
        </label>
        <div className="flex gap-1">
          {(
            Object.entries(CATEGORY_CONFIG) as [
              RuleCategory,
              (typeof CATEGORY_CONFIG)[RuleCategory],
            ][]
          ).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                onClick={() => setCategory(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors ${
                  category === key
                    ? "bg-red-600/20 border-red-500/40 text-red-300"
                    : "bg-white/[0.04] border-white/[0.08] text-white/60"
                }`}
              >
                <Icon className="w-3 h-3" />
                {t(cfg.labelKey)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Condition: "If..." */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-white/60">{t("conversionRules.create.ifLabel")}</p>
        <div className="space-y-2 pl-3 border-l-2 border-emerald-500/30">
          {Object.entries(CONDITION_LABEL_KEYS).map(([type, labelKey]) => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="condition"
                checked={conditionType === type}
                onChange={() => setConditionType(type)}
                className="accent-emerald-500"
              />
              <span className="text-xs text-white/70">{t(labelKey)}</span>
            </label>
          ))}

          {conditionType === "has_tag" && (
            <ConditionTagInput
              values={conditionTags}
              onChange={setConditionTags}
              placeholder={t("conversionRules.create.tagPlaceholder")}
            />
          )}

          {conditionType === "tag_prefix" && (
            <input
              type="text"
              value={conditionValue}
              onChange={(e) => setConditionValue(e.target.value)}
              placeholder={t("conversionRules.create.prefixPlaceholder")}
              className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
            />
          )}
        </div>
      </div>

      {/* Action: "Then..." */}
      <div className="space-y-2">
        <p className="text-xs font-medium text-white/60">{t("conversionRules.create.thenLabel")}</p>
        <div className="space-y-2 pl-3 border-l-2 border-blue-500/30">
          {Object.entries(ACTION_LABEL_KEYS).map(([type, labelKey]) => (
            <label key={type} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="action"
                checked={actionType === type}
                onChange={() => setActionType(type)}
                className="accent-blue-500"
              />
              <span className="text-xs text-white/70">{t(labelKey)}</span>
            </label>
          ))}

          {actionType === "send_with_params" && (
            <div className="space-y-2">
              <div>
                <label className="text-xs text-white/50 mb-1 block">
                  {t("conversionRules.create.eventName")}
                </label>
                <input
                  type="text"
                  value={actionEventName}
                  onChange={(e) => setActionEventName(e.target.value)}
                  placeholder={t("conversionRules.create.eventNamePlaceholder")}
                  className="w-full h-8 px-2 text-xs bg-[#030407] border border-white/[0.1] rounded-md text-white placeholder:text-white/20"
                />
              </div>
              <div>
                <label className="text-xs text-white/50 mb-1 block">
                  {t("conversionRules.create.platforms")}
                </label>
                <div className="flex gap-2">
                  {SELECTABLE_PLATFORMS.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={actionPlatforms.includes(key)}
                        onChange={() => togglePlatform(key)}
                        className="accent-red-500"
                      />
                      <span className="text-xs text-white/70">{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <Button
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white text-xs flex-1"
          disabled={createMutation.isPending || !name}
          onClick={() =>
            createMutation.mutate({
              name,
              category,
              conditionType,
              conditionValue: conditionValue || null,
              conditionTags,
              actionType,
              actionEventName: actionEventName || null,
              actionPlatforms,
            })
          }
        >
          {createMutation.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Plus className="h-3 w-3 mr-1" />
          )}
          {t("conversionRules.create.submit")}
        </Button>
        <Button size="sm" variant="ghost" className="text-xs text-white/50" onClick={onCancel}>
          {t("conversionRules.create.cancel")}
        </Button>
      </div>
      {createMutation.isError && (
        <p className="text-xs text-amber-400/70">{t("conversionRules.create.tryAgain")}</p>
      )}
    </div>
  );
}

// ─── Main Component ────────────────────────

export default function TrackingConversionRulesPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [activeTab, setActiveTab] = useState<RuleCategory>("lead");

  const { data, isLoading } = useQuery<{ rules: ConversionRule[] }>({
    queryKey: ["tracking-conversion-rules"],
    queryFn: () => apiRequest("GET", "/api/tracking/conversion-rules"),
    staleTime: 30_000,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      apiRequest("PUT", `/api/tracking/conversion-rules/${id}`, { enabled }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-conversion-rules"] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/tracking/conversion-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking-conversion-rules"] }),
  });

  const allRules = data?.rules || [];
  const filteredRules = allRules.filter((r) => r.category === activeTab);

  if (isLoading) {
    return (
      <div className="p-3 flex items-center gap-2">
        <Loader2 className="h-4 w-4 animate-spin text-red-400" />
        <span className="text-sm text-white/60">{t("conversionRules.loading")}</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium text-white/70 flex items-center gap-1.5">
          <ArrowRight className="w-3.5 h-3.5" /> {t("conversionRules.title")}
        </h3>
        {!showCreate && (
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-red-400 hover:text-red-300 h-6 px-2"
            onClick={() => setShowCreate(true)}
          >
            <Plus className="w-3 h-3 mr-1" /> {t("conversionRules.addRule")}
          </Button>
        )}
      </div>

      {/* Info */}
      <div className="p-2.5 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <p className="text-xs text-blue-300 leading-relaxed">
          <strong>{t("conversionRules.info.label")}</strong> {t("conversionRules.info.text")}
        </p>
      </div>

      {/* Category tabs */}
      <div className="flex gap-1 border-b border-white/[0.08] pb-2">
        {(
          Object.entries(CATEGORY_CONFIG) as [
            RuleCategory,
            (typeof CATEGORY_CONFIG)[RuleCategory],
          ][]
        ).map(([key, cfg]) => {
          const Icon = cfg.icon;
          const count = allRules.filter((r) => r.category === key).length;
          return (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-t border-b-2 transition-colors ${
                activeTab === key
                  ? `${cfg.color} border-current bg-white/[0.04]`
                  : "text-white/40 border-transparent hover:text-white/60"
              }`}
            >
              <Icon className="w-3 h-3" />
              {t(cfg.labelKey)}
              {count > 0 && (
                <Badge className="text-[10px] h-4 px-1 bg-white/10 text-white/60">{count}</Badge>
              )}
            </button>
          );
        })}
      </div>

      {/* Rules list */}
      {filteredRules.length > 0 ? (
        <div className="space-y-1.5">
          {filteredRules.map((rule) => {
            const ActionIcon = ACTION_ICONS[rule.actionType] || Send;
            return (
              <div
                key={rule.id}
                className={`p-3 rounded-lg border transition-colors ${
                  rule.enabled
                    ? "bg-white/[0.03] border-white/[0.08]"
                    : "bg-white/[0.01] border-white/[0.04] opacity-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <p className="text-xs font-medium text-white/80">{rule.name}</p>

                    {/* Condition */}
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-emerald-400 font-medium">
                        {t("conversionRules.if")}
                      </span>
                      <span className="text-white/60">
                        {CONDITION_LABEL_KEYS[rule.conditionType]
                          ? t(CONDITION_LABEL_KEYS[rule.conditionType])
                          : rule.conditionType}
                      </span>
                      {rule.conditionTags.length > 0 && (
                        <div className="flex gap-1">
                          {rule.conditionTags.map((t) => (
                            <span
                              key={t}
                              className="px-1.5 py-0.5 bg-emerald-500/15 text-emerald-300 rounded text-[10px] font-mono"
                            >
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                      {rule.conditionValue && (
                        <code className="text-[10px] text-emerald-400 bg-[#030407] px-1 py-0.5 rounded">
                          {rule.conditionValue}
                        </code>
                      )}
                    </div>

                    {/* Action */}
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <span className="text-blue-400 font-medium">{t("conversionRules.then")}</span>
                      <ActionIcon className="w-3 h-3 text-white/40" />
                      <span className="text-white/60">
                        {ACTION_LABEL_KEYS[rule.actionType]
                          ? t(ACTION_LABEL_KEYS[rule.actionType])
                          : rule.actionType}
                      </span>
                      {rule.actionEventName && (
                        <code className="text-[10px] text-blue-400 bg-[#030407] px-1 py-0.5 rounded">
                          {rule.actionEventName}
                        </code>
                      )}
                      {rule.actionPlatforms.length > 0 && (
                        <div className="flex gap-1">
                          {rule.actionPlatforms.map((p) => (
                            <Badge
                              key={p}
                              className="text-[10px] h-4 px-1 bg-white/10 text-white/50"
                            >
                              {PLATFORM_LABELS[p] || p}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => toggleMutation.mutate({ id: rule.id, enabled: !rule.enabled })}
                      className={`p-1 rounded hover:bg-white/[0.06] transition-colors ${rule.enabled ? "text-green-400" : "text-white/30"}`}
                      title={
                        rule.enabled ? t("conversionRules.disable") : t("conversionRules.enable")
                      }
                    >
                      <Power className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => {
                        if (confirm(t("conversionRules.confirmDelete", { name: rule.name })))
                          deleteMutation.mutate(rule.id);
                      }}
                      className="p-1 rounded hover:bg-red-500/20 text-white/30 hover:text-red-400 transition-colors"
                      title={t("conversionRules.delete")}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        !showCreate && (
          <div className="p-2.5 bg-white/[0.02] border border-dashed border-white/[0.1] rounded-lg text-center">
            <p className="text-xs text-white/40">
              {t("conversionRules.empty", { category: t(CATEGORY_CONFIG[activeTab].labelKey) })}
            </p>
          </div>
        )
      )}

      {/* Create form */}
      {showCreate && (
        <CreateConversionRuleForm
          onCreated={() => {
            setShowCreate(false);
            queryClient.invalidateQueries({ queryKey: ["tracking-conversion-rules"] });
          }}
          onCancel={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
